export const DOCS_CODE = {
  install: `npm install @draway/auth-sdk
# or
pnpm add @draway/auth-sdk`,

  init: `<span class="token-kw">import</span> { DrawayAuth } <span class="token-kw">from</span> <span class="token-str">'@draway/auth-sdk'</span>;

<span class="token-comment">// Standard zero-config initialization</span>
<span class="token-kw">const</span> auth = <span class="token-kw">new</span> <span class="token-fn">DrawayAuth</span>(
  <span class="token-str">'ak_live_your_api_key'</span>, 
  <span class="token-str">'https://auth.yourdomain.com'</span>
);`,

  phase1: `<span class="token-kw">const</span> response = <span class="token-kw">await</span> auth.<span class="token-fn">login</span>(<span class="token-str">'alex@company.com'</span>, <span class="token-str">'password123'</span>);

<span class="token-kw">if</span> (response.mfaRequired) {
  <span class="token-comment">// MFA is required. The session is strictly isolated!</span>
  <span class="token-kw">const</span> { mfaToken, preferredMethod, enabledMethods } = response;
  <span class="token-comment">// Proceed to Phase 2 with mfaToken</span>
} <span class="token-kw">else</span> {
  <span class="token-comment">// Authenticated! Cookies automatically set</span>
  console.<span class="token-fn">log</span>(<span class="token-str">'Session started:'</span>, response.user);
}`,

  phase2: `<span class="token-comment">// Submit the 6-digit code received via WhatsApp, Authenticator App, or SMS</span>
<span class="token-kw">const</span> session = <span class="token-kw">await</span> auth.<span class="token-fn">verifyLogin</span>(
  <span class="token-str">'alex@company.com'</span>, 
  <span class="token-str">'582049'</span>, 
  mfaToken, 
  <span class="token-str">'whatsapp'</span> <span class="token-comment">// 'whatsapp' | 'totp' | 'sms' | 'email'</span>
);

console.<span class="token-fn">log</span>(<span class="token-str">'Access Granted:'</span>, session.accessToken);`,

  passkey: `<span class="token-comment">// 1. Biometric Passkey Login (Supports Discoverable Credentials)</span>
<span class="token-kw">const</span> response = <span class="token-kw">await</span> auth.<span class="token-fn">loginWithPasskey</span>(<span class="token-str">'alex@company.com'</span>);

<span class="token-comment">// 2. Register New Biometric Credential (Logged-in User)</span>
<span class="token-kw">const</span> registered = <span class="token-kw">await</span> auth.<span class="token-fn">registerPasskey</span>();
<span class="token-kw">if</span> (registered) {
  alert(<span class="token-str">'Biometric passkey bound to device successfully!'</span>);
}`,

  dns: `<span class="token-comment"># Trigger Automated DNS TXT Verification</span>
curl -X POST https://api.draway.in/v1/clients/verify-domain \\
  -H <span class="token-str">"x-api-key: ak_live_your_api_key"</span>`,

  crypto: `<span class="token-comment">// AES-256-GCM Ciphertext Storage Format</span>
iv:authTag:encryptedContent

<span class="token-comment">// Example Encrypted MFA Seed in MongoDB:</span>
<span class="token-str">"e3b0c44298fc1c149afbf4c8996fb924:8b1a9953c4611296a827abf8c47804d7:72f9a2b8478491c..."</span>

<span class="token-comment">// Argon2id Password Hash Profile:</span>
<span class="token-kw">type</span>: Argon2id (Version 19)
<span class="token-kw">memoryCost</span>: 65536 KB (64 MB per hash)
<span class="token-kw">timeCost</span>: 3 iterations
<span class="token-kw">parallelism</span>: 4 threads`,

  tollFraud: `<span class="token-comment">// Enable Automated SMS Toll-Fraud Shield & Geo-Firewall</span>
<span class="token-kw">const</span> shieldConfig = {
  <span class="token-kw">geoFence</span>: {
    <span class="token-kw">blockedCountryCodes</span>: [<span class="token-str">'+248'</span>, <span class="token-str">'+252'</span>, <span class="token-str">'+672'</span>], <span class="token-comment">// High-risk premium destinations</span>
    <span class="token-kw">allowListOnly</span>: <span class="token-kw">false</span>
  },
  <span class="token-kw">circuitBreaker</span>: {
    <span class="token-kw">maxFailRatio</span>: 0.20, <span class="token-comment">// Trip if &gt;20% of OTPs fail verification</span>
    <span class="token-kw">turnstileProofOfWork</span>: <span class="token-kw">true</span>, <span class="token-comment">// Auto-challenge bot traffic</span>
    <span class="token-kw">fallbackChannel</span>: <span class="token-str">'whatsapp'</span> <span class="token-comment">// Route to WhatsApp to avoid carrier costs</span>
  },
  <span class="token-kw">deviceFingerprint</span>: {
    <span class="token-kw">enforceCanvasCheck</span>: <span class="token-kw">true</span>,
    <span class="token-kw">maxOtpPerDevicePerHour</span>: 3
  }
};`,

  whatsappConfig: `<span class="token-comment">// Trigger High-Deliverability WhatsApp OTP (Meta Cloud API)</span>
<span class="token-kw">const</span> result = <span class="token-kw">await</span> auth.<span class="token-fn">sendOtp</span>({
  <span class="token-kw">identifier</span>: <span class="token-str">'+1 (555) 234-5678'</span>,
  <span class="token-kw">channel</span>: <span class="token-str">'whatsapp'</span>, <span class="token-comment">// 98% open rate, zero telephony pumping fraud</span>
  <span class="token-kw">template</span>: <span class="token-str">'draway_auth_verification_code'</span>,
  <span class="token-kw">expiresInSeconds</span>: 300
});

console.<span class="token-fn">log</span>(<span class="token-str">'WhatsApp OTP dispatched:'</span>, result.messageId);`,

  shadowMigration: `<span class="token-comment">// Dual-Write Shadow Migration (Zero User Password Resets)</span>
<span class="token-kw">import</span> { DrawayMigrationProxy } <span class="token-kw">from</span> <span class="token-str">'@draway/auth-sdk/migrate'</span>;

<span class="token-kw">const</span> migrator = <span class="token-kw">new</span> <span class="token-fn">DrawayMigrationProxy</span>({
  <span class="token-kw">legacyProvider</span>: <span class="token-str">'auth0'</span>, <span class="token-comment">// or 'clerk' | 'cognito' | 'firebase'</span>
  <span class="token-kw">legacyDomain</span>: <span class="token-str">'https://your-tenant.us.auth0.com'</span>,
  <span class="token-kw">autoHashUpgrade</span>: <span class="token-str">'argon2id'</span> <span class="token-comment">// Silently upgrade hash on first successful login</span>
});

<span class="token-comment">// Users login seamlessly while hashes migrate silently into Draway Vault</span>
<span class="token-kw">const</span> session = <span class="token-kw">await</span> migrator.<span class="token-fn">authenticateAndMigrate</span>(email, password);`,

  impersonation: `<span class="token-comment">// Cryptographic User Impersonation (Support Mode with Audit Trail)</span>
<span class="token-kw">const</span> supportSession = <span class="token-kw">await</span> auth.<span class="token-fn">createImpersonationToken</span>({
  <span class="token-kw">targetUserId</span>: <span class="token-str">'usr_992140'</span>,
  <span class="token-kw">adminId</span>: <span class="token-str">'adm_support_agent_04'</span>,
  <span class="token-kw">ticketReference</span>: <span class="token-str">'TICKET-8492'</span>,
  <span class="token-kw">durationMinutes</span>: 15, <span class="token-comment">// Strictly time-bounded</span>
  <span class="token-kw">readOnly</span>: <span class="token-kw">true</span> <span class="token-comment">// Forbid billing, password modification</span>
});

<span class="token-comment">// Output: Cryptographically signed bearer token with immutable audit trail</span>
console.<span class="token-fn">log</span>(supportSession.token);`,

  apiKeys: `<span class="token-comment">// Generate Scoped Developer API Keys (M2M Management)</span>
<span class="token-kw">const</span> apiKey = <span class="token-kw">await</span> auth.apiKeys.<span class="token-fn">create</span>({
  <span class="token-kw">name</span>: <span class="token-str">'Production Webhook Ingestion'</span>,
  <span class="token-kw">scopes</span>: [<span class="token-str">'events:read'</span>, <span class="token-str">'webhooks:write'</span>],
  <span class="token-kw">rateLimit</span>: { <span class="token-kw">requestsPerMinute</span>: 1000 },
  <span class="token-kw">prefix</span>: <span class="token-str">'dw_live_'</span>
});

<span class="token-comment">// Key returned ONCE: 'dw_live_9f82b7c41a2948e...'</span>
<span class="token-comment">// Only SHA-256 hash is persisted in the cryptographic vault</span>`,

  edgeJwks: `<span class="token-comment">// Ultra-fast Edge JWT Verification (&lt;1ms Local Execution)</span>
<span class="token-kw">import</span> { createRemoteJWKSet, jwtVerify } <span class="token-kw">from</span> <span class="token-str">'jose'</span>;

<span class="token-kw">const</span> JWKS = <span class="token-fn">createRemoteJWKSet</span>(
  <span class="token-kw">new</span> <span class="token-fn">URL</span>(<span class="token-str">'https://auth.yourdomain.com/.well-known/jwks.json'</span>),
  { <span class="token-kw">cooldownDuration</span>: 30000, <span class="token-kw">cacheMaxAge</span>: 600000 } <span class="token-comment">// 10m local cache</span>
);

<span class="token-kw">export</span> <span class="token-kw">async</span> <span class="token-kw">function</span> <span class="token-fn">verifyTokenAtEdge</span>(token: <span class="token-kw">string</span>) {
  <span class="token-comment">// Zero network round-trips to Draway auth servers!</span>
  <span class="token-kw">const</span> { payload } = <span class="token-kw">await</span> <span class="token-fn">jwtVerify</span>(token, JWKS, {
    <span class="token-kw">issuer</span>: <span class="token-str">'https://auth.yourdomain.com'</span>
  });
  <span class="token-kw">return</span> payload;
}`
};
